// Import polyfills for better browser compatibility
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

// Expected Matrix fetch aborts must not surface as uncaught runtime errors in dev.
import './setupAbortErrorSuppression';

// Remember camera/mic + notification permission hints (localStorage, sessionStorage, cookie).
import './shared/utils/mediaDevicesPermissionPersistence';
// Call media + ringtone services (getUserMedia reuse, autoplay unlock).
import './shared/services/call/callMediaCaptureService';

import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Redirect, Switch } from "react-router-dom";
import { ROLE } from "./shared/constants/common";
import { getRedirectPath } from "./shared/utils/function";
import { getLocalItem } from "./shared/utils/config/localStorage";
import ScrollToTop from "./shared/utils/config/ScrollToTop";

// styles
import "assets/css/bootstrap.min.css";
import "assets/scss/paper-kit.scss";
import "assets/demo/demo.css";
import "assets/css/dark-mode.css";
import "assets/css/navbar-fixes.css";
import "assets/css/mobile-viewport.css";
import "assets/css/image-protection.css";
import "assets/css/font-utilities.css";
import "assets/css/font-weight-fixes.css";
import '../src/index.css'
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// pages
import Error from "views/Pages/ErrorPage/index.js";

// legal pages
import ModernLegal from "./views/Pages/legal/index.js";
import ModernPrivacy from "./views/Pages/legal/ModernPrivacy.js";
import ModernCCPA from "./views/Pages/legal/ModernCCPA.js";
import ModernContentCreatorAgreement from "./views/Pages/legal/ModernContentCreatorAgreement.js";
import ModernCopyright from "./views/Pages/legal/ModernCopyright.js";
import ModernComplianceStatement from "./views/Pages/legal/ModernComplianceStatement.js";
import ModernReferralProgramTerms from "./views/Pages/legal/ModernReferralProgramTerms.js";
import ModernRepeatInfringerPolicy from "./views/Pages/legal/ModernRepeatInfringerPolicy.js";
import ModernFAQ from "./views/Pages/faq/index.js";

import ModernContactUs from "./views/Pages/contactUs/index.js";

// Auth pages
import Login from "./views/Pages/Auth/login/index.js";
import ForgotPassword from "./views/Pages/Auth/forgotPassword/index.js";
import ResetPassword from "./views/Pages/Auth/resetPassword/index.js";

import Signup from "./views/Pages/Auth/signup/index.js";
// Creator Signup Flow Routes
import CreatorSignup from "./views/Pages/Auth/creatorSignup/index.js";
import UnderReview from "./views/Pages/Auth/creatorSignup/underReview.js";
// Subscriber Signup Flow Routes
import SubscriberSignup from "./views/Pages/Auth/subscriberSignup/index.js";
// verification
import ConfirmEmail from "./views/Pages/Auth/confirmEmail";
import ResendVerification from "./views/Pages/Auth/resendEmailVerification/index.js";

import DiscoverScreen from "./views/Pages/User/discover/index.js";
import CreatorProfile from "./views/Pages/Creator/Profile/index.js";
import AllCreators from "./views/Pages/AllCreators/index.js";
import AllCategories from "./views/Pages/AllCategories/index.js";
import ViewCreatorProfile from "./views/Pages/User/ViewCreatorProfile/index.js";
import HomeScreen from "./views/Pages/User/home/index.js";

// below are the user settings pages
import EditUsername from "./views/Pages/settings/editUsername/index.js";
import EditPassword from "./views/Pages/settings/editPassword/index.js";
import Notifications from "./views/Pages/User/notifications/index.js";
import CreatorNotifications from "./views/Pages/Creator/Notifications/index.js";
import Wallet from "./views/Pages/User/wallet/index.js";
import Transactions from "./views/Pages/User/transactions/index.js";
import Referral from "./views/Pages/Creator/Referral/index.js";
import ProfileTags from "./views/Pages/settings/ProfileTags/index.js";

// Earnings & Payout routes
import EarningsPayout from "./views/Pages/Creator/EarningsPayout/index.js";
import EarningsReport from './views/Pages/Creator/EarningsPayout/EarningsReport';
import VatNumber from "./views/Pages/Creator/VatNumber/index.js";
import GstNumber from "./views/Pages/Creator/GstNumber/index.js";
import BankAccount from "./views/Pages/Creator/BankAccount/index.js";
import W9TaxInfo from "./views/Pages/Creator/TaxInfo/w9.js";
import W8TaxInfo from "./views/Pages/Creator/TaxInfo/w8.js";
import WebView from "./views/Pages/WebView/index.js";

// pending pages
import Messaging from "./views/Pages/Message/index.js";
import ChatingPage from "./views/Pages/Message/Chating/index.js";
import CallingScreen from "./views/Pages/Message/call/CallingScreen.js";
import CallSummary from "./views/Pages/Message/call/CallSummary.js";
import NewMessage from "./views/Pages/Message/Chating/NewMessage/index.js";
import NewChatIntro from "./views/Pages/Message/Chating/NewMessage/chat.js";

import Activity from './views/Pages/Activity/index.js';

import WarningModal from "./views/Modals/WarningModal";
import CookiesConsentBanner from "./views/Modals/CookiesConsentBanner";
import PrivateRoute from "./PrivateRoute";

import { Provider } from "react-redux";
import { store } from "./shared/store";

import { initSentry, ErrorBoundary, captureException } from "shared/sentry";
import { isInAppBrowser } from "./shared/utils/config/browserUtils";

// EventEmitter polyfill for Matrix SDK - must be imported before Matrix SDK
import "./shared/utils/matrix/eventEmitterPolyfill";

// Matrix Provider and Connection - accessible for all URLs
import MatrixProvider from "./MatrixProvider";
import MatrixCallProvider from "./MatrixCallProvider.js";
import IncomingCallModal from "./views/Pages/Message/call/IncomingCallModal.js";
import { initCallRingtoneAudioUnlock } from "./shared/services/call/callRingtoneService.js";
import { initNotificationBannerSound } from "./shared/services/notificationBannerSoundService.js";

// Theme Provider
import { ThemeProvider, useTheme } from "./features/contexts/ThemeContext";

// Notification Initializer will be customized later (it will be not removed but will be customized)
import NotificationInitializer from "./components/NotificationInitializer.js";
import PushPermissionPrompt from "./components/PushPermissionPrompt";
import RouterNavigateBridge from "./components/RouterNavigateBridge.jsx";
// Signed URL Refresh Handler
import SignedUrlRefreshHandler from "./components/SignedUrlRefreshHandler.js";

// Login Time Initializer for keeping backend last-login time in sync for persisted sessions
import LoginTimeInitializer from "./components/LoginTimeInitializer.js";



// Pre-render setup: guard so failures in restricted WebViews (e.g. Instagram in-app browser) don't prevent React from mounting
try {
  if (typeof document !== 'undefined' && document.head) {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = 'https://fonts.googleapis.com';
    document.head.appendChild(link);

    const link2 = document.createElement('link');
    link2.rel = 'preconnect';
    link2.href = 'https://fonts.gstatic.com';
    link2.crossOrigin = '';
    document.head.appendChild(link2);

    const link3 = document.createElement('link');
    link3.rel = 'stylesheet';
    link3.href = 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap';
    document.head.appendChild(link3);

    const fontAwesomeScript = document.createElement('script');
    fontAwesomeScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/7.0.1/js/all.min.js';
    fontAwesomeScript.crossOrigin = 'anonymous';
    fontAwesomeScript.referrerPolicy = 'no-referrer';
    fontAwesomeScript.async = true;
    fontAwesomeScript.onerror = () => { };
    document.head.appendChild(fontAwesomeScript);
  }
} catch (_) {
  // Ignore: in-app browsers may restrict DOM or external resources
}

try {
  initSentry();
} catch (_) {
  // Don't block app if Sentry fails in restricted environments
}

try {
  // Always init — iOS needs gesture unlock listeners even in restricted WebViews.
  initCallRingtoneAudioUnlock();
  if (!isInAppBrowser()) {
    initNotificationBannerSound();
  }
} catch (_) {
  // Non-fatal: ringtones may require an explicit tap on first use
}

const resizeObserverErrorHandler = (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
    e.stopImmediatePropagation();
  }
};
window.addEventListener('error', resizeObserverErrorHandler);

function FallbackComponent() {
  const inApp = isInAppBrowser();
  const baseStyle = { padding: 24, fontFamily: 'sans-serif', background: '#fff', color: '#333', minHeight: '100vh' };
  const refreshPage = () => window.location.reload();
  return (
    <div style={baseStyle}>
      <h2 style={{ marginTop: 0 }}>Something went wrong</h2>
      {inApp ? (
        <>
          <p>
            This page works best when opened in <strong>Safari</strong>.
          </p>
          <p>
            Tap the <strong>⋯</strong> menu at the top, then choose <strong>Open in Safari</strong> (or copy the link and paste it in Safari).
          </p>
          <p>
            <a
              href={typeof window !== 'undefined' ? window.location.href : '#'}
              style={{ color: '#007AFF', wordBreak: 'break-all' }}
            >
              Open this link in Safari
            </a>
          </p>
        </>
      ) : (
        <>
          <p>An error has occurred. Please refresh the page or try again later.</p>
          <p>
            <button
              type="button"
              onClick={refreshPage}
              style={{ padding: '8px 16px', cursor: 'pointer', fontSize: 14, marginTop: 8 }}
            >
              Refresh page
            </button>
          </p>
        </>
      )}
    </div>
  );
}

/** Remove the HTML bootstrap splash as soon as React has mounted. */
function clearJhInitialSplash() {
  if (typeof window !== 'undefined') {
    if (typeof window.__JH_CLEAR_SPLASH_RELOAD__ === 'function') {
      window.__JH_CLEAR_SPLASH_RELOAD__();
    } else {
      window.__JH_APP_MOUNTED__ = true;
    }
  }
  document.getElementById('jh-initial-splash')?.remove();
  document.getElementById('jh-initial-splash-styles')?.remove();
}

// App component that uses the theme context
function App() {
  const { isDarkMode } = useTheme();

  useEffect(() => {
    clearJhInitialSplash();
  }, []);

  return (
    <MatrixProvider>
      <BrowserRouter>
        <RouterNavigateBridge />
        <MatrixCallProvider>
          <ScrollToTop />
          <div className={`App ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            <NotificationInitializer />
            <PushPermissionPrompt />
            <LoginTimeInitializer />
            <SignedUrlRefreshHandler />
            <IncomingCallModal />
            <Switch>
              {/* Root path with role-based redirect */}
              <Route
                exact
                path="/"
                render={() => {
                  const role = getLocalItem('role');
                  const roleNumber = parseInt(role, 10);
                  if (roleNumber === ROLE.CREATOR_SUB || roleNumber === ROLE.CREATOR_CONTENT) {
                    return <Redirect to="/my-profile" />;
                  } else {
                    return <Redirect to="/discover" />;
                  }
                }}
              />

              <Route
                exact
                path="/discover"
                render={() => {
                  const role = getLocalItem('role');
                  const roleNumber = parseInt(role, 10);
                  if (roleNumber === ROLE.CREATOR_SUB || roleNumber === ROLE.CREATOR_CONTENT) {
                    return <Redirect to="/my-profile" />;
                  } else {
                    return <DiscoverScreen />;
                  }
                }}
              />

              <Route
                path="/404"
                render={(props) => <Error {...props} />}
              />

              {/* Modern Legal Routes */}
              <Route
                exact
                path="/termsofservice"
                render={(props) => <ModernLegal {...props} />}
              />
              <Route
                exact
                path="/privacy"
                render={(props) => <ModernPrivacy {...props} />}
              />
              <Route
                exact
                path="/ccpa"
                render={(props) => <ModernCCPA {...props} />}
              />
              <Route
                exact
                path="/creator-agreement"
                render={(props) => <ModernContentCreatorAgreement {...props} />}
              />
              <Route
                exact
                path="/copyright"
                render={(props) => <ModernCopyright {...props} />}
              />
              <Route
                exact
                path="/compliance"
                render={(props) => <ModernComplianceStatement {...props} />}
              />
              <Route
                exact
                path="/referral-terms"
                render={(props) => <ModernReferralProgramTerms {...props} />}
              />
              <Route
                exact
                path="/infringer"
                render={(props) => <ModernRepeatInfringerPolicy {...props} />}
              />

              {/* Modern FAQ Route */}
              <Route
                exact
                path="/faq"
                render={(props) => <ModernFAQ {...props} />}
              />

              <Route
                exact
                path="/contactus"
                render={(props) => <ModernContactUs {...props} />}
              />

              <Route
                path="/help"
                render={(props) => <ModernContactUs {...props} />}
              />

              {/* Modern Authentication Routes */}
              <Route
                exact
                path="/login"
                name="Login Page"
                render={(props) => <Login {...props} />}
              />
              <Route
                exact
                path="/forgot-password"
                name="Forgot Password Page"
                render={(props) => <ForgotPassword {...props} />}
              />
              <Route
                exact
                path="/resetPassword"
                name="Reset Password Page"
                render={(props) => <ResetPassword {...props} />}
              />

              {/* signup process */}
              <Route
                exact
                path="/signup"
                name="Signup Page"
                render={(props) => <Signup {...props} />}
              />

              {/* Creator Signup Flow Routes */}
              <Route
                exact
                path="/creator-signup"
                name="Creator Signup Page"
                render={(props) => <CreatorSignup {...props} />}
              />
              <Route
                exact
                path="/under-review"
                name="Under Review Page"
                render={(props) => <UnderReview {...props} />}
              />

              {/* Subscriber Signup Flow Routes */}
              <Route
                exact
                path="/subscriber-signup"
                name="Subscriber Signup Page"
                render={(props) => <SubscriberSignup {...props} />}
              />

              <Route
                exact
                path="/confirm/:userId"
                name="Confirm"
                render={(props) => <ConfirmEmail {...props} />}
              />

              <Route
                exact
                path="/resend-verification"
                name="Resend Verification"
                render={(props) => <ResendVerification {...props} />}
              />

              {/* for all users */}
              <Route
                exact
                path="/all-creators"
                render={(props) => <AllCreators {...props} />}
              />
              <Route
                exact
                path="/all-categories"
                render={(props) => <AllCategories {...props} />}
              />
              <PrivateRoute
                exact
                path="/webview"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT, ROLE.USER]}
                component={(props) => <WebView {...props} />}
              />

              {/* for creator and subscriber both users can access this routes */}
              <PrivateRoute
                exact
                path="/edit-username"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT, ROLE.USER]}
                component={(props) => <EditUsername {...props} />}
              />
              <PrivateRoute
                exact
                path="/change-password"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT, ROLE.USER]}
                component={(props) => <EditPassword {...props} />}
              />
              <PrivateRoute
                exact
                path="/profile-tags"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT, ROLE.USER]}
                component={(props) => <ProfileTags {...props} />}
              />



              {/* for creator */}
              <PrivateRoute
                path="/my-profile"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT]}
                component={(props) => <CreatorProfile {...props} />}
              />
              <PrivateRoute
                exact
                path="/my-notifications"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT]}
                component={(props) => <CreatorNotifications {...props} />}
              />
              <PrivateRoute
                exact
                path="/referrals"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT]}
                component={(props) => <Referral {...props} />}
              />
              <PrivateRoute
                exact
                path="/earnings-payout"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT]}
                component={(props) => <EarningsPayout {...props} />}
              />
              <PrivateRoute
                exact
                path="/earnings-report/:year"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT]}
                component={(props) => <EarningsReport {...props} />}
              />
              <PrivateRoute
                exact
                path="/vat-number"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT]}
                component={(props) => <VatNumber {...props} />}
              />
              <PrivateRoute
                exact
                path="/gst-number"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT]}
                component={(props) => <GstNumber {...props} />}
              />
              <PrivateRoute
                exact
                path="/bank-account"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT]}
                component={(props) => <BankAccount {...props} />}
              />
              <PrivateRoute
                exact
                path="/bank-account/edit"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT]}
                component={(props) => <BankAccount {...props} />}
              />
              <PrivateRoute
                exact
                path="/w9-tax-info"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT]}
                component={(props) => <W9TaxInfo {...props} />}
              />
              <PrivateRoute
                exact
                path="/w8-tax-info"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT]}
                component={(props) => <W8TaxInfo {...props} />}
              />

              {/* for subscriber */}
              <PrivateRoute
                exact
                path="/feed"
                allowRole={[ROLE.USER]}
                component={(props) => <HomeScreen {...props} />}
              />
              <PrivateRoute
                exact
                path="/subscriptions"
                allowRole={[ROLE.USER]}
                component={(props) => <HomeScreen {...props} />}
              />
              <PrivateRoute
                exact
                path="/purchased"
                allowRole={[ROLE.USER]}
                component={(props) => <HomeScreen {...props} />}
              />
              <PrivateRoute
                exact
                path="/notifications"
                allowRole={[ROLE.USER]}
                component={(props) => <Notifications {...props} />}
              />
              <PrivateRoute
                exact
                path="/wallet"
                allowRole={[ROLE.USER]}
                component={(props) => <Wallet {...props} />}
              />
              <PrivateRoute
                exact
                path="/transactions"
                allowRole={[ROLE.USER]}
                component={(props) => <Transactions {...props} />}
              />


              {/* pending route for customize code */}
              <PrivateRoute
                path="/my/messages/chat/:userId"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT, ROLE.USER]}
                component={(props) => <ChatingPage key={props.match.params.userId} {...props} />}
              />
              <PrivateRoute
                path="/my/messages/new/:userId"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT, ROLE.USER]}
                component={(props) => <NewChatIntro {...props} />}
              />
              <PrivateRoute
                path="/my/messages"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT, ROLE.USER]}
                component={(props) => <Messaging {...props} />}
              />
              <PrivateRoute
                path="/call/summary"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT, ROLE.USER]}
                component={(props) => <CallSummary {...props} />}
              />
              <PrivateRoute
                path="/call"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT, ROLE.USER]}
                component={(props) => <CallingScreen {...props} />}
              />
              <PrivateRoute
                path="/new-message"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT, ROLE.USER]}
                component={(props) => <NewMessage {...props} />}
              />

              <PrivateRoute
                exact
                path="/activity"
                allowRole={[ROLE.CREATOR_SUB, ROLE.CREATOR_CONTENT, ROLE.USER]}
                component={(props) => <Activity {...props} />}
              />
              <Route
                path="/referral/:username"
                render={({ match }) => (
                  <Redirect
                    to={`/creator-signup?referral=${match.params.username}`}
                  />
                )}
              />

              {/* Dynamic profile route should be last among specific routes */}
              <Route
                exact
                path="/:creatorUsername"
                render={(props) => <ViewCreatorProfile {...props} />}
              />

              <Route
                path="*"
                render={() => {
                  const role = getLocalItem('role');
                  const redirectPath = getRedirectPath(role);
                  return <Redirect to={redirectPath} />;
                }}
              />

            </Switch >
            <WarningModal />
            <CookiesConsentBanner />
          </div >
          <ToastContainer
            enableMultiContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
          <ToastContainer
            enableMultiContainer
            containerId="push-header-notifications"
            position="top-center"
            autoClose={4500}
            hideProgressBar
            newestOnTop
            closeOnClick={false}
            rtl={false}
            pauseOnFocusLoss
            draggable={false}
            pauseOnHover
            theme="dark"
            className="push-header-toast-host"
          />
        </MatrixCallProvider>
      </BrowserRouter >
    </MatrixProvider >
  );
}

const container = document.getElementById("root");
if (!container) {
  document.body.innerHTML = '<div style="padding:24px;font-family:sans-serif;background:#fff;color:#333;min-height:100vh;">Failed to load app. Please open this link in Safari.</div>';
} else {
  const root = createRoot(container);
  try {
    root.render(
      <Provider store={store}>
        <ThemeProvider>
          <ErrorBoundary fallback={FallbackComponent}>
            <App />
          </ErrorBoundary>
        </ThemeProvider>
      </Provider>
    );
    clearJhInitialSplash();
  } catch (mountError) {
    // In restricted WebViews (e.g. Instagram in-app browser), React may throw during mount
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
    container.innerHTML = [
      '<div style="padding:24px;font-family:sans-serif;background:#fff;color:#333;min-height:100vh;">',
      '<h2 style="margin-top:0;">Something went wrong</h2>',
      '<p>This page works best when opened in <strong>Safari</strong>.</p>',
      '<p>Tap the <strong>⋯</strong> menu at the top, then choose <strong>Open in Safari</strong> (or copy the link and paste it in Safari).</p>',
      currentUrl ? `<p><a href="${currentUrl.replace(/"/g, '&quot;')}" style="color:#007AFF;word-break:break-all;">${currentUrl}</a></p>` : '',
      '</div>'
    ].join('');
    captureException(
      mountError instanceof Error ? mountError : new Error(String(mountError)),
      { tags: { context: 'app_mount' } }
    );
  }
}
